import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import '../../modules/SettingsDetailPage.css';
import '../../backlog/backlog.css';
import './fieldIntake.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { AuditTrailButton } from '../../../components/AuditTrailButton';
import { showDialog, useConfirm, useSaveFlow } from '../../../components/confirm';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';
import { BusyIcon, LoaderSymbol, runLoading } from '../../../components/loading';
import { useNotify } from '../../../components/notifications';
import { TopbarStepper } from '../../../components/StepIsland/TopbarStepper';
import type { StepState } from '../../../components/StepIsland/StepIsland';
import { UsDateTimeInput } from '../../../components/UsDateTimeInput';
import { guardedExit, useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { navigateToPage } from '../../../layouts/navigateTo';
import { fmtLocalDate, fmtLocalDateTime } from '../../../utils/datetime';
import {
  createFieldIntakeJob, downloadFieldIntakeWorkbook, fetchFieldIntakeDetail, fetchFieldIntakePhotoUrl,
  openFieldIntakeReport, saveFieldIntakeReceipt,
} from './api';
import {
  arr, arrivalGps, auditLines, BUILDING, categoryName, dueState, onSiteDuration, ELEVATIONS, APP_TESTS, chosenTests, SIG_COVERS, errorText, FIELD_INTAKE_FORM, intakePill, obj, photoGroup, reviewLevels, roomsOf, sampleLines, siteRows, str, strs,
  type SampleLine,
} from './fieldIntakeLogic';
import type { CheckLevel, FieldIntakeCheck, FieldIntakeDetail, FieldIntakeLookups, FieldIntakePhoto, FieldIntakeSignature, Permissions } from './types';

interface Props {
  serviceTaskId: string;
  /** The list's visible order, for the prev/next arrows. */
  ids: string[];
  perm: Permissions;
  lookups: FieldIntakeLookups | null;
  onNavigate: (id: string) => void;
  onBack: () => void;
}

type Tab = 'val' | 'site' | 'rooms' | 'samples' | 'photos' | 'coc' | 'sigs' | 'docs' | 'audit';

const CUSTODY = ['Sampler retains custody', 'Transferred to courier', 'Received by laboratory', 'Direct to mobile lab'];
const CONDITIONS = ['Intact, sealed', 'Bag damaged — contents intact', 'Leaking / compromised'];
const TEMPERATURES = ['Ambient', 'Chilled', 'Not applicable'];
const SIG_NAMES: Record<string, string> = { coc: 'Chain of Custody', survey: 'Surveyor declaration', sampling: 'Sampling declaration' };

interface Receipt {
  custodyStatus: string; receivedBy: string; receivedAt: string; receiptCondition: string;
  temperature: string; samplesReceived: string; notes: string;
}

/** ISO date-time from the API → `yyyy-mm-ddTHH:mm` for UsDateTimeInput (wall clock as stored). */
const toInput = (iso: string | null) => (iso ? iso.slice(0, 16) : '');

function receiptOf(d: FieldIntakeDetail | null): Receipt {
  const h = d?.header;
  return {
    custodyStatus: h?.custodyStatus ?? 'Received by laboratory',
    receivedBy: h?.receivedBy ?? '',
    receivedAt: toInput(h?.receivedAt ?? null),
    receiptCondition: h?.receiptCondition ?? '',
    temperature: h?.temperature ?? '',
    samplesReceived: h?.samplesReceived != null ? String(h.samplesReceived) : '',
    notes: h?.intakeNotes ?? '',
  };
}

/** One read-only side-label field (BO UI Standard: label beside the field, 50/50 column-major). */
function Ro({ label, value, req, multi }: { label: string; value: ReactNode; req?: boolean; multi?: boolean }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div className={`sd-row${multi ? ' fi-row-top' : ''}`}>
      <label className="sd-label">{label}{req && <span className="req">*</span>}</label>
      <div className={`sd-input readonly fi-ro${multi ? ' fi-ro-multi' : ''}`}>{empty ? <span className="fi-soft">—</span> : value}</div>
    </div>
  );
}

/**
 * Sampling Management ▸ Field Intake &amp; Job Creation — DETAIL VIEW for one
 * field service: what LIMS holds for the service (from LIMS), what the
 * technician recorded (from the ALPACA Field app), the laboratory receipt (the
 * only part edited here), and the child tabs. Create Job ID creates the ONE
 * LIMS Job for the service (server re-checks everything) and then offers to
 * open it in Sample Registration.
 */
export default function FieldIntakeDetailPage({ serviceTaskId, ids, perm, lookups, onNavigate, onBack }: Props) {
  const [d, setD]               = useState<FieldIntakeDetail | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [tab, setTab]           = useState<Tab>('val');
  const [rc, setRc]             = useState<Receipt>(receiptOf(null));
  const [creating, setCreating] = useState(false);
  const [showPassed, setShowPassed] = useState(false);
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [allOptions, setAllOptions] = useState(false);
  const [auditSrc, setAuditSrc] = useState<'all' | 'Field app' | 'LIMS'>('all');
  const [fold, setFold]         = useState<{ svc: boolean; visit: boolean }>({ svc: false, visit: false });
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [bigPhoto, setBigPhoto] = useState<FieldIntakePhoto | null>(null);
  const [highlight, setHighlight] = useState<string | null>(null);
  const notify = useNotify();
  const { alert } = useConfirm();
  const saveFlow = useSaveFlow();
  const tabsRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const det = await fetchFieldIntakeDetail(serviceTaskId);
      setD(det);
      setRc(receiptOf(det));
    } catch (e) {
      setError(errorText(e, 'Could not load the field service.'));
    } finally {
      setLoading(false);
    }
  }, [serviceTaskId]);

  useEffect(() => { void load(); setTab('val'); setShowPassed(false); setIssuesOnly(false); }, [load]);

  // Photographs: fetched with the session's token, shown as object URLs, revoked on leave.
  useEffect(() => {
    if (!d) return undefined;
    let alive = true;
    const made: string[] = [];
    void (async () => {
      for (const p of d.photos) {
        if (!p.clientPhotoId) continue;
        try {
          const url = await fetchFieldIntakePhotoUrl(serviceTaskId, p.clientPhotoId);
          made.push(url);
          if (alive) setPhotoUrls(u => ({ ...u, [p.clientPhotoId!]: url }));
        } catch { /* a missing image shows as a placeholder tile, not an error */ }
      }
    })();
    return () => { alive = false; made.forEach(u => URL.revokeObjectURL(u)); setPhotoUrls({}); };
  }, [d?.header.serviceTaskId, d?.photos.length]);   // eslint-disable-line react-hooks/exhaustive-deps

  const h = d?.header;
  const jobDone = !!h?.jobID;
  const canEdit = perm.canWrite && !jobDone;
  const baseline = useMemo(() => receiptOf(d), [d]);
  const dirty = !!d && canEdit && JSON.stringify(rc) !== JSON.stringify(baseline);
  useUnsavedGuard(dirty);

  // The receipt check follows the form while it has unsaved edits, so Validate checks what is
  // on screen (as the prototype does) instead of throwing the edits away. Create Job ID still
  // asks for the receipt to be saved first.
  const receiptEntered = !!rc.receivedBy && !!rc.receivedAt;
  const checks = (d?.checks ?? []).map(c => (c.key === 'receipt' && dirty
    ? { ...c, level: (receiptEntered ? 'ok' : 'err') as CheckLevel,
        detail: receiptEntered ? 'Entered — save it to record it' : 'Fill in Laboratory receipt above' }
    : c));
  const errs = checks.filter(c => c.level === 'err' && !jobDone);
  const warns = checks.filter(c => c.level === 'warn');
  const lines = useMemo(() => sampleLines(d?.record ?? null, d?.ecoc ?? null), [d]);
  const samples = useMemo(() => lines.filter(l => l.first), [lines]);
  const record = d?.record ?? null;
  const ecoc = d?.ecoc ?? null;
  const due = dueState(h?.dueDate);
  const collectedDay = str(obj(ecoc?.collection).dateSamplesCollected) || str(obj(record?.coc).collected);
  /** A time-only value (the record's "16:37") with its collection day, in the G6 MM/DD/YYYY HH:mm form. */
  const onCollectionDay = (t: string | null | undefined): string =>
    (t && /^\d{1,2}:\d{2}$/.test(t) && collectedDay ? `${collectedDay} ${t}` : t ?? '');
  const idx = ids.indexOf(serviceTaskId);
  const prevId = idx > 0 ? (ids[idx - 1] ?? null) : null;
  const nextId = idx >= 0 && idx < ids.length - 1 ? (ids[idx + 1] ?? null) : null;

  const tabIssue = (t: Tab): string | null => {
    const hits = checks.filter(c => (c.go ?? '').split(':')[0] === t);
    return hits.some(c => c.level === 'err') ? 'err' : hits.some(c => c.level === 'warn') ? 'warn' : null;
  };

  // ── Actions ────────────────────────────────────────────────────────────────
  const close = () => void guardedExit(onBack);
  const go = (id: string | null) => { if (id) void guardedExit(() => onNavigate(id)); };

  const save = async () => {
    if (!canEdit || !d) return;
    const n = rc.samplesReceived.trim() === '' ? null : Number(rc.samplesReceived);
    if (n !== null && (!Number.isInteger(n) || n < 0)) {
      await alert({ title: 'Error', message: 'Samples received must be a whole number.', tone: 'error' });
      return;
    }
    await saveFlow({
      confirmTitle: 'Confirm',
      confirmMessage: 'Do you want to Save?',
      formName: FIELD_INTAKE_FORM,
      auditAction: d.header.intakeVersion ? 'edit' : 'create',
      save: async () => {
        await saveFieldIntakeReceipt(serviceTaskId, {
          version: d.header.intakeVersion,
          custodyStatus: rc.custodyStatus || null,
          receivedBy: rc.receivedBy || null,
          receivedAt: rc.receivedAt || null,
          receiptCondition: rc.receiptCondition || null,
          temperature: rc.temperature || null,
          samplesReceived: n,
          notes: rc.notes.trim() || null,
        });
      },
      successMessage: 'Saved successfully.',
      onSaved: () => { void load(); },
    });
  };

  /** What validation found: the error list, or a pass (an info message while warnings remain). */
  const report = (all: FieldIntakeCheck[]) => {
    const e = all.filter(c => c.level === 'err');
    const w = all.filter(c => c.level === 'warn');
    if (e.length) void cannotCreate(e);
    else if (w.length) notify.info(`Validation passed with ${w.length} warning${w.length === 1 ? '' : 's'} to review.`);
    else notify.success('Validation passed. Ready to create the Job ID.');
  };

  // Validate re-reads the service from LIMS when nothing is unsaved; with unsaved receipt
  // edits it checks what is on screen and keeps them.
  const lastValidate = useRef(false);
  const validate = async () => {
    setTab('val');
    tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (dirty) { report(checks); return; }
    lastValidate.current = true;
    await load();
  };

  // After Validate's reload, report what it found (the flag is set only by Validate).
  useEffect(() => {
    if (!lastValidate.current || !d) return;
    lastValidate.current = false;
    report(d.checks);
  }, [d]);   // eslint-disable-line react-hooks/exhaustive-deps

  /** The shared error dialog: "The Job ID cannot be created yet. Complete:" and the checks as its list. */
  const cannotCreate = (failed: FieldIntakeCheck[]) => showDialog({
    type: 'error', title: 'Error', message: 'The Job ID cannot be created yet. Complete:', list: failed.map(c => c.text),
  });

  /** LIMS Sample ID: JobID-NNN of the Job's real SampleNo (matched by the field sample #), else "on creation". */
  const limsSampleId = (l: SampleLine): string => {
    if (!h?.jobID) return 'on creation';
    const no = d?.jobSamples.find(s => s.clientSampleID === l.sampleId)?.sampleNo
            ?? (d?.jobSamples.length ? null : l.sampleNo);
    return no == null ? '—' : `${h.jobID}-${String(no).padStart(3, '0')}`;
  };

  const startCreate = async () => {
    if (!d || !h || jobDone) return;
    if (dirty) {
      await alert({ title: 'Save first', tone: 'warning', message: 'The laboratory receipt has unsaved changes. Save them, then create the Job ID.' });
      return;
    }
    if (errs.length) {
      setTab('val');
      await cannotCreate(errs);
      return;
    }
    const tests = strs(obj(ecoc?.analysis).tests);
    // Warnings ride in the message with the Validation tab's "!" — the dialog's own list
    // marks every row ✗ (an error), and warnings never block the Job.
    const yes = await showDialog({
      type: 'confirm', variant: 'record', title: 'Confirm',
      message: (
        <>
          {`Create the Laboratory Job for ${h.serviceNo}? LIMS assigns the Job ID when you confirm and registers ${samples.length} sample${samples.length === 1 ? '' : 's'} for ${tests.join(', ') || 'no tests'}. The field record, photos, signatures and eCOC are linked to it.`}
          {warns.length > 0 && (
            <div className="fi-dlg-warns">
              {warns.map(w => <div key={w.key}><span className="fi-m warn">!</span>{w.text}</div>)}
            </div>
          )}
        </>
      ),
      rows: samples.map(l => ({
        label: `-${String(l.sampleNo).padStart(3, '0')}`,
        value: [l.sampleId, lines.filter(x => x.sampleId === l.sampleId).map(x => x.readsAs).filter(Boolean).join('; ')].filter(Boolean).join(' · '),
      })),
      confirmLabel: 'Yes', cancelLabel: 'No',
    });
    if (yes === true) await createJob();
  };

  const createJob = async () => {
    if (!h) return;
    setCreating(true);
    try {
      const j = await runLoading(createFieldIntakeJob(serviceTaskId, h.reviewStamp), { blocking: true, label: 'Creating the Job…' });
      await load();
      setTab('val');
      const range = j.sampleCount > 1 ? `${j.jobID}-001 to ${j.jobID}-${String(j.sampleCount).padStart(3, '0')}` : `${j.jobID}-001`;
      const open = await showDialog({
        type: 'success', title: 'Job ID created',
        message: `Job ID ${j.jobID} was created for ${j.serviceNo} and ${j.sampleCount} sample${j.sampleCount === 1 ? ' was' : 's were'} registered (${range}). Do you want to open this Job in Sample Registration to continue?`,
        note: [
          j.attachments.length ? `Attached to the Job: ${j.attachments.join(', ')}.` : '',
          j.attachError ?? '',
          j.unmappedTests.length ? `Add these tests in Sample Registration: ${j.unmappedTests.join(', ')}.` : '',
        ].filter(Boolean).join(' ') || undefined,
        actions: [{ label: 'Stay here', value: 'stay' }, { label: 'Open Sample Registration', primary: true, value: 'open' }],
      });
      if (open === 'open') navigateToPage('sampreg', j.jobID);
    } catch (e) {
      const failed = (e as { response?: { data?: { error?: { checks?: FieldIntakeCheck[] } } } })?.response?.data?.error?.checks;
      if (failed?.length) await cannotCreate(failed);
      else await alert({ title: 'Error', tone: 'error', message: errorText(e, 'The Job could not be created.') });
      await load();
    } finally {
      setCreating(false);
    }
  };

  const preview = async () => {
    try {
      const shown = await runLoading(openFieldIntakeReport(serviceTaskId), { blocking: true, label: 'Composing the report…' });
      if (!shown) notify.warning('The browser blocked the report window. Allow pop-ups for this site, then try again.');
    } catch (e) { notify.error(errorText(e, 'Could not open the report.')); }
  };

  const workbook = async () => {
    if (!h?.serviceNo) return;
    try {
      await runLoading(downloadFieldIntakeWorkbook(serviceTaskId, h.serviceNo), { blocking: true, label: 'Building the eCOC workbook…' });
      notify.success(`eCOC workbook ${h.serviceNo}.xlsx downloaded.`);
    }
    catch (e) { notify.error(errorText(e, 'Could not download the eCOC.')); }
  };

  /** "Go to" from a check: the receipt field, Job settings, or the tab (and the sample row). */
  const goFix = (goTo: string | null) => {
    if (!goTo) return;
    const [t, item] = goTo.split(':');
    if (t === 'receipt') {
      const id = !rc.receivedBy ? 'fi-rb' : 'fi-ra';
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (document.getElementById(id) as HTMLElement | null)?.focus();
      setHighlight(id);
      setTimeout(() => setHighlight(null), 1800);
      return;
    }
    if (t === 'service') { setFold(f => ({ ...f, svc: false })); document.getElementById('fi-sec-svc')?.scrollIntoView({ behavior: 'smooth' }); return; }
    setTab(t as Tab);
    if (t === 'samples') setIssuesOnly(true);
    setHighlight(item ? `fi-s-${item}` : null);
    setTimeout(() => setHighlight(null), 1800);
    tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useHotkeys([
    { combo: 'mod+s', label: 'Save', group: 'Field Intake', order: 1, disabled: !canEdit, run: () => { void save(); } },
    { combo: 'alt+v', label: 'Validate', group: 'Field Intake', order: 2, disabled: loading, run: () => { void validate(); } },
    { combo: 'alt+p', label: 'Report preview', group: 'Field Intake', order: 3, disabled: !h?.ecocNo, run: () => { void preview(); } },
    { combo: 'alt+left', label: 'Previous record', group: 'Field Intake', order: 4, disabled: !prevId, run: () => go(prevId) },
    { combo: 'alt+right', label: 'Next record', group: 'Field Intake', order: 5, disabled: !nextId, run: () => go(nextId) },
    { combo: 'esc', label: 'Cancel', group: 'Field Intake', order: 9, allowInInput: false, run: close },
  ]);

  // ── Action bar (top and bottom) ────────────────────────────────────────────
  const why = jobDone ? <>Job <b>{h?.jobID}</b> created</>
    : errs.length ? `${errs.length} check${errs.length === 1 ? '' : 's'} to pass before the Job ID`
    : warns.length ? `Ready · ${warns.length} warning${warns.length === 1 ? '' : 's'} to review` : 'Ready to create the Job ID';
  const ActionBar = ({ bottom }: { bottom?: boolean }) => (
    <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
      <span className="fi-why">{loading ? '' : why}</span>
      <button className="sd-abtn" onClick={() => void save()} disabled={!canEdit}
              title={!perm.canWrite ? 'You do not have Write permission on this form' : `Save (${formatCombo('mod+s')})`}>
        <span className="tb-i">💾</span> Save
      </button>
      <button className="sd-abtn" onClick={() => { void validate(); }} disabled={loading} title={`Validate (${formatCombo('alt+v')})`}>
        <span className="tb-i">✓</span> Validate
      </button>
      <button className="sd-abtn" onClick={() => void workbook()} disabled={!h?.ecocNo} title="The eCOC workbook: Chain of Custody, Samples, Photographs">
        <span className="tb-i">📄</span> eCOC
      </button>
      <button className="sd-abtn" onClick={() => void preview()} disabled={!h?.ecocNo} title={`Report preview (${formatCombo('alt+p')})`}>
        <span className="tb-i">👁</span> Report preview
      </button>
      <button className="sd-abtn primary" onClick={() => void startCreate()} disabled={!perm.canWrite || jobDone || creating || loading}
              title={!perm.canWrite ? 'You do not have Write permission on this form' : jobDone ? `Job ${h?.jobID} already created` : 'Create the LIMS Job for this service'}>
        {creating ? <BusyIcon busy icon="ti-check" /> : <span className="tb-i">✓</span>} Create Job ID
      </button>
      <span className="sd-abtn-sep" aria-hidden="true" />
      <button className="sd-abtn cancel" onClick={close} title={`Cancel (${formatCombo('esc')})`}>
        <span className="tb-i">↩︎</span> Cancel
      </button>
    </div>
  );

  const stepAt = jobDone ? 3 : errs.length ? (tab === 'val' ? 1 : 0) : 2;
  const steps = ['Review', 'Validate', 'Create Job ID'].map((label, i) => ({
    id: String(i), label, state: (i < stepAt ? 'done' : i === stepAt ? 'active' : 'todo') as StepState,
  }));

  // ── Tabs ───────────────────────────────────────────────────────────────────
  const rooms = roomsOf(record, ecoc);
  const tabs: { id: Tab; label: string; n?: number }[] = [
    { id: 'val', label: 'Validation' }, { id: 'site', label: 'Site Description' },
    { id: 'rooms', label: 'Rooms', n: rooms.length }, { id: 'samples', label: 'Samples', n: samples.length },
    { id: 'photos', label: 'Photos', n: d?.photos.length ?? 0 }, { id: 'coc', label: 'Chain of Custody' },
    { id: 'sigs', label: 'Signatures', n: d?.signatures.filter(s => s.isCurrent).length ?? 0 },
    { id: 'docs', label: 'eCOC & Report' }, { id: 'audit', label: 'Audit Trail', n: d ? auditLines(record, d.audit).length : 0 },
  ];

  // The global grid standard (CLAUDE.md standing rules 7 and 9): plain black cells in the grid's own
  // font — no bold, monospace, grey or red text, no in-cell images — and colour ONLY in the status
  // column (Check). A cell whose value is an action uses the grid's own link (adg-celllink bl-link).
  const sampleCols: AppColumn<SampleLine>[] = [
    { field: 'sampleId', header: 'Sample #', width: 80, text: l => (l.first ? l.sampleId : '↳') },
    { field: 'lims', header: 'LIMS Sample ID', width: 130,
      text: l => (l.first ? limsSampleId(l) : '') },
    { field: 'format', header: 'Format', width: 70, text: l => (l.first ? l.format : `material ${l.rowNo} of ${l.rows}`) },
    // An empty HA reads "missing" as in the prototype, but does not make the sample Incomplete or block
    // the Job: the field app lets it be left empty and the lab cannot edit sample data (owner, 2026-10-15).
    { field: 'ha', header: 'HA', width: 80, text: l => (l.first ? l.ha || 'missing' : '') },
    { field: 'location', header: 'Location', width: 130, text: l => (l.first ? l.location || 'missing' : '') },
    { field: 'material', header: 'Material Sampled', width: 160, text: l => l.material || (l.first ? 'missing' : '') },
    { field: 'materialType', header: 'Material Type', width: 140 },
    { field: 'color', header: 'Material Color', width: 110 },
    { field: 'texture', header: 'Texture / Pattern', width: 120 },
    { field: 'friable', header: 'Friable or Non-Friable', width: 140 },
    { field: 'amount', header: 'Amount Sampled', width: 110 },
    { field: 'condition', header: 'Material Condition', width: 120, text: l => (l.first ? l.condition : '') },
    { field: 'qty', header: 'Approx. Sq / Ln Ft.', width: 110, align: 'right', text: l => (l.first ? l.qty : '') },
    { field: 'collectedAt', header: 'Collected', width: 130, text: l => (l.first ? onCollectionDay(l.collectedAt) : '') },
    { field: 'photoCount', header: 'Photos', width: 90,
      text: l => (l.first ? (l.photoCount ? `${l.photoCount} photo${l.photoCount === 1 ? '' : 's'}` : 'none') : ''),
      render: l => (!l.first ? null : l.photoCount
        ? <button type="button" className="adg-celllink bl-link" title="Open the photos"
                  onClick={e => { e.stopPropagation(); setTab('photos'); }}>{l.photoCount} photo{l.photoCount === 1 ? '' : 's'}</button>
        : 'none') },
    { field: 'readsAs', header: 'Reads to the laboratory as', width: 280 },
    { field: 'pct', header: 'Percentage & Asbestiform', width: 160, text: () => 'filled by the lab' },
    { field: 'check', header: 'Check', width: 100, text: l => (l.first ? (l.missing.length ? 'Incomplete' : 'Complete') : ''),
      render: l => (l.first ? <span className={`bl-pill ${l.missing.length ? 'alert' : 'ok'}`}>{l.missing.length ? 'Incomplete' : 'Complete'}</span> : null) },
  ];

  const panel = (): ReactNode => {
    if (!d || !h) return null;
    if (tab === 'val') {
      const bad = [...checks.filter(c => c.level === 'err'), ...checks.filter(c => c.level === 'warn')];
      const ok = checks.filter(c => c.level === 'ok');
      const Row = (c: FieldIntakeCheck) => (
        <div className="fi-ck" key={c.key}>
          <span className={`fi-m ${c.level}`}>{c.level === 'ok' ? '✓' : c.level === 'warn' ? '!' : '✗'}</span>
          <span>{c.text}<br /><small>{c.detail}</small></span>
          {c.go && c.level !== 'ok' && !jobDone ? <button className="fi-link" onClick={() => goFix(c.go)}>Go to</button> : <span />}
          <span className={`bl-pill ${c.level === 'ok' ? 'ok' : c.level === 'warn' ? 'warn' : 'alert'}`}>{c.level === 'ok' ? 'Passed' : c.level === 'warn' ? 'Warning' : 'To fix'}</span>
        </div>
      );
      return (
        <>
          <div className={`fi-note ${jobDone ? 'ok' : errs.length ? 'err' : warns.length ? 'warn' : 'ok'}`}>
            {jobDone ? <>Job <b>{h.jobID}</b> was created for this service. The field record, photos, signatures and eCOC stay linked to it.</>
              : errs.length ? <><b>{errs.length} check{errs.length === 1 ? '' : 's'} must pass</b> before the Job ID can be created. Use <b>Go to</b> to jump to each one.</>
              : warns.length ? <><b>All required checks pass.</b> Review {warns.length} warning{warns.length === 1 ? '' : 's'}, then create the Job ID.</>
              : <><b>All checks pass.</b> Ready to create the Job ID.</>}
          </div>
          {jobDone ? <div className="fi-chk">{checks.map(Row)}</div> : (
            <>
              <div className="fi-chk">{bad.map(Row)}</div>
              <button className="fi-passed" aria-expanded={showPassed} onClick={() => setShowPassed(s => !s)}>
                <span className="fi-m ok">✓</span>{ok.length} passed<span className="fi-car">{showPassed ? '▾ Hide' : '▸ Show'}</span>
              </button>
              {showPassed && <div className="fi-chk">{ok.map(Row)}</div>}
            </>
          )}
        </>
      );
    }
    if (tab === 'site') {
      const siteR = siteRows(record, ecoc);
      const building = obj(ecoc?.building ?? obj(record?.survey).bldg);
      const cert = str(obj(ecoc?.collection).surveyorCertificate) || str(obj(record?.survey).cert);
      const template = str(obj(ecoc?.collection).inspectionTemplate) || h.surveyTemplateName || '';
      return (
        <>
          <div className="sd-child-bar fi-childbar">
            <span>Inspection sheet: <b>{template || '—'}</b></span>
            <label className="fi-toggle"><input type="checkbox" checked={allOptions} onChange={e => setAllOptions(e.target.checked)} /> Show all options</label>
          </div>
          <div className="sd-grid sd-grid--side">
            {siteR.map(r => (
              <Ro key={r.label} label={r.label} req={r.required} multi
                  value={allOptions
                    ? <><span className="fi-opts">{[...r.options, ...r.values.filter(v => !r.options.includes(v))].map(o => (
                        <span key={o} className={`fi-opt${r.values.includes(o) ? ' on' : ''}`}>{o}</span>))}</span>
                        {r.other && <span className="fi-soft">· Other: {r.other}</span>}</>
                    : r.values.length || r.other ? <>{r.values.join(', ')}{r.other && <span className="fi-soft">{r.values.length ? ' · ' : ''}Other: {r.other}</span>}</>
                    : (r.required ? <span className="fi-miss">Not answered</span> : '')} />
            ))}
            <Ro label="Surveyor certification #" value={cert} req={/washington/i.test(template)} />
          </div>
          <h3 className="sd-section-h">Additional building information</h3>
          <div className="sd-grid sd-grid--side">
            {BUILDING.map(b => <Ro key={b.key} label={b.label} value={str(building[b.key])} />)}
          </div>
        </>
      );
    }
    if (tab === 'rooms') {
      const roomPhotos = d.photos.filter(p => photoGroup(p) === 'Rooms');
      type RoomRow = { name: string; added: boolean; samples: number; photos: number };
      const rr: RoomRow[] = rooms.map(r => ({
        ...r,
        samples: samples.filter(s => s.location === r.name).length,
        photos: roomPhotos.filter(p => (p.caption ?? '').toLowerCase().startsWith(r.name.toLowerCase())).length,
      }));
      return (
        <>
          <div className="sd-child-bar fi-childbar">Ticked sample locations, plus rooms the technician added on site.</div>
          <AppDataGrid<RoomRow> rows={rr} getRowId={r => r.name} maximizable exportFilename={`${h.serviceNo}-rooms`}
            columns={[
              { field: 'name', header: 'Room', width: 200 },
              { field: 'added', header: 'Source', width: 160, text: r => (r.added ? 'Added on site' : 'Sample location') },
              { field: 'samples', header: 'Samples', width: 90, align: 'right', type: 'number' },
              { field: 'photos', header: 'Room photos', width: 110, align: 'right', type: 'number' },
              { field: 'coverage', header: 'Coverage', width: 150, text: r => (r.photos ? 'Photographed' : 'No overall photo'),
                render: r => <span className={`bl-pill ${r.photos ? 'ok' : 'warn'}`}>{r.photos ? 'Photographed' : 'No overall photo'}</span> },
            ]} emptyText="No rooms recorded." />
        </>
      );
    }
    if (tab === 'samples') {
      const shown = issuesOnly ? lines.filter(l => l.missing.length || !samples.find(s => s.sampleId === l.sampleId)?.photoCount) : lines;
      return (
        <>
          <div className="sd-child-bar fi-childbar">
            <span>{samples.length} sample{samples.length === 1 ? '' : 's'} · <b>2A, 2B</b> = one set · extra rows = more materials in one sample · LIMS Sample ID = Job ID + sample # · Percentage &amp; Asbestiform is filled by the laboratory</span>
            <label className="fi-toggle"><input type="checkbox" checked={issuesOnly} onChange={e => setIssuesOnly(e.target.checked)} /> Show issues only</label>
          </div>
          <AppDataGrid<SampleLine> rows={shown} columns={sampleCols} getRowId={l => l.key} maximizable
            exportFilename={`${h.serviceNo}-samples`} emptyText={issuesOnly ? 'No sample has an issue.' : 'No samples.'}
            getRowClassName={l => (highlight === `fi-s-${l.sampleId}` && l.first ? 'adg-selected' : undefined)} />
        </>
      );
    }
    if (tab === 'photos') {
      const groups: ('Exterior' | 'Rooms' | 'Samples')[] = ['Exterior', 'Rooms', 'Samples'];
      const roomPh = d.photos.filter(p => photoGroup(p) === 'Rooms');
      const covered = rooms.filter(r => roomPh.some(p => (p.caption ?? '').toLowerCase().startsWith(r.name.toLowerCase()))).length;
      const elevations = ELEVATIONS.filter(e => d.photos.some(p => photoGroup(p) === 'Exterior' && (p.caption ?? '') === e)).length;
      const collected = str(obj(ecoc?.collection).dateSamplesCollected) || str(obj(record?.coc).collected);
      const title = { Exterior: 'Exterior · elevations', Rooms: 'Rooms · overall', Samples: 'Samples · close-up' };
      return (
        <>
          <div className="sd-child-bar fi-childbar">Each photo carries the time it was taken and the GPS position. Stored in LIMS (field-photos). Click one to enlarge.</div>
          {groups.map(g => {
            const list = d.photos.filter(p => photoGroup(p) === g);
            return (
              <div key={g} className="fi-phgroup">
                <h4>{title[g]} <span className="sd-assoc-tabn">{list.length}</span>
                  {g === 'Exterior' && <span className="fi-soft fi-normal">· {elevations} of {ELEVATIONS.length} standard elevations</span>}
                  {g === 'Rooms' && <span className={`bl-pill ${covered === rooms.length ? 'ok' : 'warn'}`}>Room coverage {covered} of {rooms.length}</span>}
                </h4>
                {list.length ? (
                  <div className="fi-photos">
                    {list.map(p => (
                      <button key={p.id} className="fi-ph" onClick={() => setBigPhoto(p)}>
                        <div className="fi-img"><span className="fi-k">{g === 'Samples' ? 'Sample' : g === 'Rooms' ? 'Room' : 'Exterior'}{p.sampleId ? ` · Sample ${p.sampleId}` : ''}</span>
                          {p.clientPhotoId && photoUrls[p.clientPhotoId]
                            ? <img src={photoUrls[p.clientPhotoId]} alt={p.caption ?? 'Photograph'} />
                            : <span>{p.caption || 'Photograph'}</span>}</div>
                        <div className="fi-cap">{p.caption || (p.sampleId ? `Sample ${p.sampleId}` : g)}</div>
                        <div className="fi-meta">{[[collected, p.takenTime].filter(Boolean).join(' '), p.gps && `GPS ${p.gps}`].filter(Boolean).join(' · ')}</div>
                      </button>
                    ))}
                  </div>
                ) : <div className="fi-note">None taken.</div>}
              </div>
            );
          })}
        </>
      );
    }
    if (tab === 'coc') {
      const coc = obj(record?.coc);
      const analysis = obj(ecoc?.analysis);
      const client = obj(ecoc?.client);
      const project = obj(ecoc?.project);
      return (
        <div className="sd-grid sd-grid--side">
          <Ro label="Name / Company" value={str(client.name) || h.clientName} />
          <Ro label="Contact Name" value={str(client.contact) || h.contactName} />
          <Ro label="Contact Email" value={str(client.email) || h.contactEmail} />
          <Ro label="Project Name" value={str(project.name) || h.projectName} />
          <Ro label="Job Name" value={str(project.jobName) || h.quoteNo} />
          <Ro label="Project Address" value={str(project.address) || h.siteAddress} />
          <Ro label="Date Samples Collected" req value={str(obj(ecoc?.collection).dateSamplesCollected) || str(coc.collected)} />
          <Ro label="# of Samples" value={String(samples.length)} />
          <Ro label="Test selection" req multi value={(() => {
            const chosen = chosenTests(strs(analysis.tests), strs(coc.tests));
            const offered = APP_TESTS.map(t => t.label);
            return <span className="fi-opts">{[...offered, ...chosen.filter(t => !offered.includes(t))].map(t => (
              <span key={t} className={`fi-opt${chosen.includes(t) ? ' on' : ''}`}>{t}</span>))}</span>;
          })()} />
          <Ro label="Turnaround time" req value={str(analysis.turnaround) || str(coc.tat)} />
          <Ro label="Relinquished by" req value={str(coc.signedBy) || str(coc.relinquished)} />
          <Ro label="COC signed" value={(() => {
            const sig = d.signatures.find(s => s.isCurrent && (s.kind ?? '').toLowerCase() === 'coc');
            if (sig?.signedAt) return fmtLocalDateTime(sig.signedAt);
            return coc.signed === true ? str(coc.relinquished) : 'Not signed';
          })()} />
          <Ro label="Received by (laboratory)" value={h.receivedByName} />
          <Ro label="Samples received (laboratory)" value={h.samplesReceived != null ? `${h.samplesReceived} of ${samples.length}` : ''} />
        </div>
      );
    }
    if (tab === 'sigs') {
      const current = d.signatures.filter(s => s.isCurrent);
      const card = (s: FieldIntakeSignature) => {
        const st = d.signatureStates.find(x => x.kind === (s.kind ?? '').toLowerCase());
        const valid = st?.valid !== false;
        return (
        <div className="fi-sig" key={s.id}>
          <h4>{SIG_NAMES[(s.kind ?? '').toLowerCase()] ?? s.kind}<span className={`bl-pill ${valid ? 'ok' : 'alert'}`}>{valid ? 'Valid' : 'No longer valid'}</span></h4>
          {!valid && st && <div className="fi-kv">Changed since signing: <b>{st.changed.join(', ')}</b></div>}
          <div className="fi-mark">{s.markPng ? <img src={s.markPng} alt={`Signature of ${s.signerName ?? s.signedByName ?? ''}`} /> : <span className="fi-soft">no mark</span>}</div>
          <div className="fi-kv">Signed by <b>{s.signerName ?? s.signedByName}</b>
            {str(obj(obj(record?.sigs)[(s.kind ?? '').toLowerCase()]).userId) && <span className="fi-soft"> · user {str(obj(obj(record?.sigs)[(s.kind ?? '').toLowerCase()]).userId)}</span>}</div>
          <div className="fi-kv">Signed at <b>{s.signedAt ? fmtLocalDateTime(s.signedAt) : '—'}</b></div>
          <div className="fi-kv">Password checked by <b>{(s.verifiedBy ?? '').toLowerCase() === 'device' ? 'Device (signed offline)' : 'LIMS'}</b></div>
          {SIG_COVERS[(s.kind ?? '').toLowerCase()] && <div className="fi-kv">Covers: {SIG_COVERS[(s.kind ?? '').toLowerCase()]!(samples.length)}</div>}
          {s.meaning && <div className="fi-kv">Meaning: {s.meaning}</div>}
          <div className="fi-kv fi-mono">SHA-256 {s.payloadSha256 ? `${s.payloadSha256.slice(0, 12)}…` : '—'}</div>
        </div>
        );
      };
      return (
        <>
          <div className="sd-child-bar fi-childbar">Each signature fingerprints the record it signed (SHA-256). A change afterwards makes it invalid.</div>
          <div className="fi-sigs">{current.length ? current.map(card) : <div className="fi-note err">No signatures recorded.</div>}</div>
        </>
      );
    }
    if (tab === 'docs') {
      return (
        <div className="sd-grid sd-grid--side">
          <Ro label="eCOC number" value={h.ecocNo ? <span className="fi-mono">{h.ecocNo}</span> : ''} />
          <Ro label="eCOC issued" value={h.ecocIssuedAt ? fmtLocalDateTime(h.ecocIssuedAt) : 'Not issued — issued once the record is Synced'} />
          <Ro label="eCOC SHA-256" value={h.ecocSha256 ? <span className="fi-mono">{h.ecocSha256.slice(0, 12)}…</span> : ''} />
          <Ro label="eCOC workbook" value={h.ecocNo ? <button className="fi-link" onClick={() => void workbook()}>3 sheets: Chain of Custody · Samples · Photographs</button> : ''} />
          <Ro label="Report template" value={h.reportTemplateName} />
          <Ro label="Report version / state" value={h.reportVersion != null
            ? <>v{h.reportVersion} · <span className={`bl-pill ${h.reportApprovedAt ? 'ok' : 'info'}`}>{h.reportApprovedAt ? 'Approved' : 'Generated'}</span></> : ''} />
          <Ro label="Report generated" value={h.reportGeneratedAt ? fmtLocalDateTime(h.reportGeneratedAt) : ''} />
          <Ro label="Report issued (by / at)" value={h.reportApprovedAt ? [h.reportApprovedByName, fmtLocalDateTime(h.reportApprovedAt)].filter(Boolean).join(' · ') : ''} />
        </div>
      );
    }
    // audit
    const all = auditLines(record, d.audit).filter(a => auditSrc === 'all' || a.source === auditSrc);
    type A = (typeof all)[number] & { k: string };
    const rowsA: A[] = all.map((a, i) => ({ ...a, k: String(i) }));
    return (
      <>
        <div className="sd-child-bar fi-childbar">
          <span>Field app entries travel with the record; LIMS adds its own.</span>
          <select className="sd-input fi-srcsel" aria-label="Source" value={auditSrc} onChange={e => setAuditSrc(e.target.value as typeof auditSrc)}>
            <option value="all">All sources</option><option value="Field app">Field app</option><option value="LIMS">LIMS</option>
          </select>
        </div>
        <AppDataGrid<A> rows={rowsA} getRowId={a => a.k} maximizable exportFilename={`${h.serviceNo}-audit`} emptyText="No audit entries."
          columns={[
            { field: 'when', header: 'Date & time', width: 150, text: a => (a.source === 'LIMS' && a.when ? fmtLocalDateTime(a.when) : a.when) },
            { field: 'action', header: 'Action', width: 220 },
            { field: 'by', header: 'By', width: 140 },
            { field: 'detail', header: 'Detail', width: 380 },
            { field: 'source', header: 'Source', width: 100 },
          ]} />
      </>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const eta = obj(record?.eta);
  const schedDate = h?.scheduledDate ? fmtLocalDate(h.scheduledDate) : '';
  const wf = obj(record?.wf);
  const fld = (id: string) => d?.fields.find(f => f.fieldId === id)?.value ?? '';

  return (
    <div className="bl-page mlv-host fi-detail">
      <div className="bl-grid-area">
        <div className="sd-wrap">
          <div className="sd-head">
            <div>
              <h2>
                Field Intake – {h?.serviceNo ?? '…'}
                {d && <span className={`bl-pill ${intakePill(d.intakeStatus)}`}>{d.intakeStatus}</span>}
                {d && !jobDone && due && <span className={`bl-pill ${due.overdue ? 'alert' : due.today ? 'warn' : 'info'}`}
                  title={h?.dueEstimated ? 'Estimated: counted from the collection time until the receipt is recorded.' : 'The due date the Job gets: received date and time plus the turnaround, in working days.'}>
                  {due.overdue ? 'Overdue · ' : 'Due '}{fmtLocalDateTime(h?.dueDate)}</span>}
                {jobDone && <span className="bl-pill ok">Job <b className="fi-mono">{h?.jobID}</b></span>}
                {dirty && <span className="fi-dirty">Unsaved changes</span>}
                {h && <AuditTrailButton variant="chip" formName={FIELD_INTAKE_FORM} sourceId={h.serviceTaskId}
                                        onClick={() => { setTab('audit'); tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} />}
              </h2>
              <div className="sd-bc">
                <span>Home</span><i className="ti ti-chevron-right" />
                <span>Sampling Management</span><i className="ti ti-chevron-right" />
                <a onClick={close}>Field Intake &amp; Job Creation</a><i className="ti ti-chevron-right" />
                <span>{h?.serviceNo}</span>
              </div>
            </div>
            <div className="sd-navarrows">
              <button className="sd-na" title={`Previous (${formatCombo('alt+left')})`} disabled={!prevId} onClick={() => go(prevId)}>‹</button>
              <button className="sd-na next" title={`Next (${formatCombo('alt+right')})`} disabled={!nextId} onClick={() => go(nextId)}>›</button>
            </div>
          </div>

          <TopbarStepper steps={steps} isClickable={() => false} />

          <ActionBar />

          <div className="sd-body">
            {loading && !d ? (
              <div className="sd-loading"><LoaderSymbol size={20} /> Loading…</div>
            ) : error || !d || !h ? (
              <div className="bl-err"><i className="ti ti-alert-circle" /> {error ?? 'Not found.'}</div>
            ) : (
              <>
                <h3 className="sd-section-h fi-sec" id="fi-sec-svc">
                  <button className="fi-sec-t" aria-expanded={!fold.svc} onClick={() => setFold(f => ({ ...f, svc: !f.svc }))}>
                    <span className="fi-car">{fold.svc ? '▸' : '▾'}</span>Service &amp; request</button>
                  <span className="fi-src lims">from LIMS</span>
                </h3>
                {fold.svc ? (
                  <div className="fi-sum">
                    {([['Client', h.clientName], ['Project', [h.projectCode, h.projectName].filter(Boolean).join(' · ')], ['Site', h.siteAddress],
                       ['Scheduled', [schedDate, h.scheduledTime].filter(Boolean).join(' ')], ['Turnaround', h.turnAroundName],
                       ['Tests requested', fld('methods')]] as [string, string | null][])
                      .map(([k, v]) => <span key={k}>{k}: <b>{v || '—'}</b></span>)}
                    <button className="fi-link" onClick={() => setFold(f => ({ ...f, svc: false }))}>Show all fields</button>
                  </div>
                ) : (
                  <div className="sd-grid sd-grid--side">
                    <Ro label="Service ID" value={<b className="fi-mono">{h.serviceNo}</b>} />
                    <Ro label="Request ID" value={<span className="fi-mono">{h.requestNo}</span>} />
                    <Ro label="Category / service type" value={[categoryName(h.categoryCode), h.serviceType].filter(Boolean).join(' · ')} />
                    <Ro label="Survey template" value={h.surveyTemplateName} />
                    <Ro label="Client" req value={h.clientName ? <b>{h.clientName}</b> : ''} />
                    <Ro label="Client contact" value={[h.contactName, h.contactEmail, h.contactPhone].filter(Boolean).join(' · ')} />
                    <Ro label="Project ID" req value={h.projectCode} />
                    <Ro label="Project name" value={h.projectName} />
                    <Ro label="Job name (Quote ID)" value={h.quoteNo} />
                    <Ro label="Site address" req value={h.siteAddress ? <><i className="ti ti-map-pin fi-pin" aria-hidden="true" />{h.siteAddress}</> : ''} />
                    <Ro label="Site contact" value={[fld('jsName') || fld('propName'), fld('jsPhone') || fld('propPhone'), str(record?.hoEmail)].filter(Boolean).join(' · ')} />
                    <Ro label="Scheduled" value={[schedDate, h.scheduledTime].filter(Boolean).join(' ')} />
                    <Ro label="Duration" value={onSiteDuration(h.arrivedAt, h.completedAt) ? `${onSiteDuration(h.arrivedAt, h.completedAt)} on site` : ''} />
                    <Ro label="Turnaround (request)" value={h.turnAroundName} />
                    <Ro label="Tests requested" value={fld('methods')} />
                    <Ro label="Sample plan" value={fld('samPlan')} />
                    <Ro label="Linked sampling visit" value={h.parentServiceNo} />
                    <Ro label="Scope / instructions" multi value={h.instructions || h.requestDescription} />
                  </div>
                )}

                <h3 className="sd-section-h fi-sec">
                  <button className="fi-sec-t" aria-expanded={!fold.visit} onClick={() => setFold(f => ({ ...f, visit: !f.visit }))}>
                    <span className="fi-car">{fold.visit ? '▸' : '▾'}</span>Field visit</button>
                  <span className="fi-src app">from the ALPACA Field app</span>
                </h3>
                {fold.visit ? (
                  <div className="fi-sum">
                    {([['Technician', h.technicianName], ['Field record', [h.recordStage, h.recordVersion != null ? `version ${h.recordVersion}` : ''].filter(Boolean).join(' · ')],
                       ['Arrived', h.arrivedAt ? fmtLocalDateTime(h.arrivedAt) : str(record?.arrived)],
                       ['Validated', [str(wf.validatedBy), str(wf.validatedAt)].filter(Boolean).join(' · ')]] as [string, string | null][])
                      .map(([k, v]) => <span key={k}>{k}: <b>{v || '—'}</b></span>)}
                    <button className="fi-link" onClick={() => setFold(f => ({ ...f, visit: false }))}>Show all fields</button>
                  </div>
                ) : (
                  <div className="sd-grid sd-grid--side">
                    <Ro label="Technician" value={h.technicianName} />
                    <Ro label="Field record" value={<><span className="bl-pill info">{h.recordStage}</span> version {h.recordVersion ?? '—'}{h.recordSavedAt ? ` · saved ${fmtLocalDateTime(h.recordSavedAt)}` : ''}</>} />
                    <Ro label="ETA given" value={str(eta.ch) ? <><b>{str(eta.ch).charAt(0).toUpperCase() + str(eta.ch).slice(1)}</b> to {[str(eta.name), str(eta.to), [schedDate, str(eta.at)].filter(Boolean).join(' '), str(eta.outcome)].filter(Boolean).join(' · ')}</> : ''} />
                    <Ro label="ETA attempts" value={strs(record?.etaTries).length ? strs(record?.etaTries).join('; ') : arr(record?.etaTries).map(t => [str(obj(t).name), str(obj(t).at)].filter(Boolean).join(' at ')).filter(Boolean).join('; ')} />
                    <Ro label="ETA message" multi value={str(eta.msg)} />
                    <Ro label="ETA subject (email)" value={str(eta.subj)} />
                    <Ro label="Arrived on site" value={[h.arrivedAt ? fmtLocalDateTime(h.arrivedAt) : str(record?.arrived), arrivalGps(record) && `GPS ${arrivalGps(record)}`].filter(Boolean).join(' · ')} />
                    <Ro label="Review levels" value={reviewLevels(record)} />
                    <Ro label="Submitted" value={[str(wf.submittedBy), str(wf.submittedAt)].filter(Boolean).join(' · ')} />
                    <Ro label="Validated" value={[str(wf.validatedBy), str(wf.validatedAt)].filter(Boolean).join(' · ')} />
                  </div>
                )}

                <h3 className="sd-section-h fi-sec" id="fi-receipt">Laboratory receipt <span className="fi-src lab">entered at intake</span></h3>
                <div className="sd-grid sd-grid--side">
                  <div className="sd-row">
                    <label className="sd-label" htmlFor="fi-cs">Custody status<span className="req">*</span></label>
                    <select id="fi-cs" className="sd-input" value={rc.custodyStatus} disabled={!canEdit}
                            onChange={e => setRc(r => ({ ...r, custodyStatus: e.target.value }))}>
                      {CUSTODY.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="sd-row">
                    <label className="sd-label" htmlFor="fi-rb">Received by<span className="req">*</span></label>
                    <select id="fi-rb" className={`sd-input${!rc.receivedBy ? ' required-empty' : ''}${highlight === 'fi-rb' ? ' fi-hl' : ''}`}
                            value={rc.receivedBy} disabled={!canEdit} onChange={e => setRc(r => ({ ...r, receivedBy: e.target.value }))}>
                      <option value="">Select</option>
                      {(lookups?.staff ?? []).map(s => <option key={s.oid} value={s.oid}>{s.name}</option>)}
                      {h.receivedBy && !(lookups?.staff ?? []).some(s => s.oid === h.receivedBy) && <option value={h.receivedBy}>{h.receivedByName}</option>}
                    </select>
                  </div>
                  <div className="sd-row">
                    <label className="sd-label" htmlFor="fi-ra">Received date &amp; time<span className="req">*</span></label>
                    <div className={`fi-dt${!rc.receivedAt ? ' fi-empty' : ''}${highlight === 'fi-ra' ? ' fi-hl' : ''}`}>
                      <UsDateTimeInput id="fi-ra" className="sd-input" mode="datetime" value={rc.receivedAt} readOnly={!canEdit}
                                       onChange={v => setRc(r => ({ ...r, receivedAt: v }))} />
                    </div>
                  </div>
                  <div className="sd-row">
                    <label className="sd-label" htmlFor="fi-sr">Samples received</label>
                    <div className="fi-ctl">
                      <input id="fi-sr" className="sd-input" type="number" min={0} inputMode="numeric" value={rc.samplesReceived} readOnly={!canEdit}
                             placeholder="Count the sample bags" onChange={e => setRc(r => ({ ...r, samplesReceived: e.target.value }))} />
                      <span className="fi-of">of {samples.length} on the COC</span>
                    </div>
                  </div>
                  <div className="sd-row">
                    <label className="sd-label" htmlFor="fi-co">Condition on receipt</label>
                    <select id="fi-co" className="sd-input" value={rc.receiptCondition} disabled={!canEdit}
                            onChange={e => setRc(r => ({ ...r, receiptCondition: e.target.value }))}>
                      <option value="">Select</option>
                      {CONDITIONS.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="sd-row">
                    <label className="sd-label" htmlFor="fi-te">Temperature</label>
                    <select id="fi-te" className="sd-input" value={rc.temperature} disabled={!canEdit}
                            onChange={e => setRc(r => ({ ...r, temperature: e.target.value }))}>
                      <option value="">Select</option>
                      {TEMPERATURES.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="sd-row">
                    <label className="sd-label" htmlFor="fi-no">Intake notes</label>
                    <input id="fi-no" className="sd-input" value={rc.notes} readOnly={!canEdit} maxLength={1000}
                           placeholder="Anything the lab should know" onChange={e => setRc(r => ({ ...r, notes: e.target.value }))} />
                  </div>
                </div>

                <div ref={tabsRef} className="sd-assoc-tabbar" role="tablist" style={{ marginTop: 18 }}>
                  {tabs.map(t => {
                    const issue = t.id === 'val' ? (jobDone ? 'ok' : errs.length ? 'err' : warns.length ? 'warn' : 'ok') : tabIssue(t.id);
                    return (
                      <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
                              className={`sd-assoc-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
                        {issue && <span className={`fi-dot ${issue}`} aria-hidden="true" />}
                        {t.label}{t.n != null && <span className="sd-assoc-tabn">{t.n}</span>}
                      </button>
                    );
                  })}
                </div>
                <div className={`sd-childtabpanel${tab === 'rooms' || tab === 'samples' || tab === 'audit' ? '' : ' fi-pane'}`} role="tabpanel">{panel()}</div>
              </>
            )}
          </div>

          <ActionBar bottom />
        </div>
      </div>

      {bigPhoto && (
        <AppDialog open title={bigPhoto.caption || 'Photograph'} icon="ti-photo" width={620} onClose={() => setBigPhoto(null)}>
          <div className="fi-big">{bigPhoto.clientPhotoId && photoUrls[bigPhoto.clientPhotoId]
            ? <img src={photoUrls[bigPhoto.clientPhotoId]} alt={bigPhoto.caption ?? 'Photograph'} /> : <span>Image not available</span>}</div>
          <div className="sd-grid sd-grid--side" style={{ marginTop: 10 }}>
            <Ro label="Caption" value={bigPhoto.caption} />
            <Ro label="Taken" value={onCollectionDay(bigPhoto.takenTime)} />
            <Ro label="GPS" value={bigPhoto.gps} />
            <Ro label="Kind" value={[({ Samples: 'Sample', Rooms: 'Room', Exterior: 'Exterior' } as const)[photoGroup(bigPhoto)],
                                     bigPhoto.sampleId && `sample ${bigPhoto.sampleId}`].filter(Boolean).join(' · ')} />
          </div>
        </AppDialog>
      )}

    </div>
  );
}
